import { useDisableBodyScroll } from '@common';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  animated,
  AnimationResult,
  SpringValue,
  useSpringRef,
  useTransition
} from '@react-spring/web';
import clsx from 'clsx';
import { useKeyHandler } from 'components/components';
import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import HlIconButton from '@hl-components/IconButton/IconButton';
import Tooltip from 'rc-tooltip';

export const DrawerContext = createContext<{
  isDrawerOpen: (id: string) => boolean;
  drawerDict: Record<string, boolean>;
  toggleDrawer: (id: string) => void;
  openDrawer: (id: string) => void;
  closeDrawer: (id: string) => void;
  setOnCloseCallback: (id: string, onClose: () => void) => void;
}>({
  isDrawerOpen: () => false,
  drawerDict: {},
  toggleDrawer: () => {},
  openDrawer: () => {},
  closeDrawer: () => {},
  setOnCloseCallback: () => {}
});

export const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  return context;
};

type DrawerProps = PropsWithChildren<{
  id: string;
  header?: string | undefined | ReactNode;
  title?: string | undefined | ReactNode;
  subTitle?: ReactNode | undefined;
  className?: string | undefined;
  icon?: IconProp | undefined;
  width?: number;
  onClose?: () => void;
  onTransitionFinished?: () => void;
}>;

export const Drawer = ({
  children,
  header,
  title,
  subTitle,
  className,
  id,
  icon,
  width = 700,
  onClose,
  onTransitionFinished
}: DrawerProps) => {
  const { isDrawerOpen, drawerDict, closeDrawer, setOnCloseCallback } =
    useDrawerContext();

  const transRef = useSpringRef();

  const transitions = useTransition(drawerDict[id], {
    from: { width: 0, opacity: 0 },
    enter: { width: width, opacity: 1 },
    leave: { width: 0, opacity: 0 },
    onRest: (result: AnimationResult) => {
      const { value } = result as AnimationResult<
        SpringValue<{
          width: number;
          opacity: number;
        }>
      >;
      Boolean(result.finished) &&
        value.width === width &&
        value.opacity === 1 &&
        onTransitionFinished?.();
    }
  });

  useDisableBodyScroll(isDrawerOpen(id));

  useKeyHandler(document, 'Escape', 'keydown', () => {
    closeDrawer(id);
  });

  useEffect(() => {
    transRef.start({
      width: drawerDict[id] ? width : 0,
      opacity: drawerDict[id] ? 1 : 0
    });
  }, [drawerDict]);

  useEffect(() => {
    if (drawerDict[id] && onClose) {
      setOnCloseCallback(id, onClose);
    }
  }, [drawerDict[id]]);

  return transitions((style, isOpen) =>
    isOpen ? (
      createPortal(
        <>
          <animated.div
            onClick={() => {
              closeDrawer(id);
            }}
            className={clsx('drawer-background', className)}
            style={{ opacity: style.opacity }}
          />
          <animated.div
            className={clsx('slide-in-drawer', className)}
            style={{ width: style.width }}
          >
            <animated.div
              className="card"
              data-testid={('card-' + title).replaceAll(' ', '')}
              style={{
                padding: '1rem 1.5rem 1rem 1.5rem',
                opacity: style.opacity,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <header className="pb-4 mb-4">
                <div className="is-flex is-align-items-center is-justify-content-space-between">
                  {icon && (
                    <span
                      className="card-header-icon mr-2"
                      style={{
                        width: '1.5rem',
                        height: '1.5rem'
                      }}
                    >
                      <FontAwesomeIcon icon={icon} size="xl" />
                    </span>
                  )}
                  <div className="is-flex is-flex-direction-column">
                    {header && <p className="is-fs-12">{header}</p>}
                    <p className="card-header-title p-0 is-fs-20 hl-text-icons-primary has-text-weight-semibold">
                      {title ?? 'Comments'}
                    </p>
                  </div>
                  <span
                    onClick={() => closeDrawer(id)}
                    className="card-header-icon p-0 icon has-text-dark is-clickable"
                    aria-label="close-drawer"
                    style={{
                      width: '2rem',
                      height: '2rem',
                      borderRadius: '1.5rem',
                      background: 'var(--hl-subtle-light)'
                    }}
                  >
                    <Tooltip overlay="Close" placement="left" trigger="hover">
                      <span>
                        <HlIconButton
                          icon={faTimes}
                          size="large"
                          color="subtle"
                          styleVariant="tertiary"
                        />
                      </span>
                    </Tooltip>
                  </span>
                </div>
                {subTitle && (
                  <div className="hl-text-icons-primary is-fs-12">
                    {subTitle}
                  </div>
                )}
              </header>
              <div
                id="content"
                className="card-content p-0 is-flex is-flex-direction-column is-justify-content-space-between"
                style={{ flex: 1 }}
              >
                {children}
              </div>
            </animated.div>
          </animated.div>
        </>,
        document.body,
        id
      )
    ) : (
      <></>
    )
  );
};

export const DrawerProvider = ({ children }: PropsWithChildren) => {
  const [drawerDict, setDrawerDict] = useState<Record<string, boolean>>({});

  const onCloseCallbacksRef = useRef<Record<string, (() => void) | undefined>>(
    {}
  );

  const setOnCloseCallback = (id: string, onClose: () => void) => {
    onCloseCallbacksRef.current[id] = onClose;
  };

  const toggleDrawer = (id: string) => {
    setDrawerDict((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!isDrawerOpen(id)) {
      if (onCloseCallbacksRef.current[id]) {
        onCloseCallbacksRef.current[id]!();
      }
    }
  };

  const isDrawerOpen = (id: string) => Boolean(drawerDict[id]);

  const openDrawer = (id: string) =>
    setDrawerDict((prev) => ({ ...prev, [id]: true }));

  const closeDrawer = (id: string) => {
    setDrawerDict((prev) => ({ ...prev, [id]: false }));
    if (onCloseCallbacksRef.current[id]) {
      onCloseCallbacksRef.current[id]!();
    }
  };

  return (
    <DrawerContext.Provider
      value={{
        isDrawerOpen,
        drawerDict,
        toggleDrawer,
        openDrawer,
        closeDrawer,
        setOnCloseCallback
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};
